/**
 * The Knowledge Archive canvas's colours, light and dark.
 *
 * Literal colours, not tokens: a canvas takes an `rgb()` string, not a class,
 * so the theme branch happens in JavaScript (`canvasPalette`). Lifted out of
 * `MindspaceGraph` so the renderer reads as rendering.
 *
 * ## Indigo for what you learned, grey for what you have not
 *
 * The legend has four states and they are not one scale. Three are about a
 * memory you HAVE: learned, fading, worth a review. The fourth is the absence
 * of one. So:
 *
 * - **Learned** is deep indigo, the brand at full strength.
 * - **Fading** is the same indigo, washed out: lighter and paler, a learned
 *   colour on its way to grey. The encoding explains itself.
 * - **Worth a review** is amber, the one hue that asks for something.
 * - **Not studied** is a neutral grey-lavender. It carries no brand colour
 *   because there is no memory for it to describe.
 *
 * The previous version put fading and not studied on adjacent steps of the
 * same indigo ramp (`#6467f2` and `#828df8`). Different in lightness only,
 * and at a 3px dot they read as one colour. Now they differ in CHROMA as
 * well, which is what the eye separates first at small sizes: one is
 * coloured, the other is not. ΔE 43 in light, 40 in dark.
 *
 * ## Measured, on `--card` (`#F4F6FF` light / `#1B1D23` dark)
 *
 * | state          | light  | dark    |
 * | -------------- | ------ | ------- |
 * | learned        | 7.33:1 | 5.35:1  |
 * | fading         | 3.17:1 | 10.54:1 |
 * | not studied    | 2.62:1 | 3.18:1  |
 * | worth a review | 4.66:1 | 8.81:1  |
 *
 * `not studied` in light sits just under the 3:1 graphical minimum, on
 * purpose: it is 96% of a typical archive, and a map that should recede
 * behind what you have learned cannot paint its majority at full weight.
 *
 * `graphPalette.test.ts` asserts those ratios and separations rather than the
 * hex strings. A test that repeats a constant only proves it was copied
 * twice.
 */

import type { MemoryTier } from '@/lib/mindspace/memoryTier';

/**
 * Memory state. `fresh` draws as `strong`: "learned this week" is recency,
 * not strength, and a fifth colour would promise a distinction the eye cannot
 * make at this size.
 */
export const NODE_TIER_LIGHT: Record<MemoryTier, string> = {
  strong: '#4338ca', // indigo-700
  fresh: '#4338ca',
  fading: '#7c7ff0', // the same hue, washed out
  new: '#9497bd', // neutral grey-lavender: no memory, so no brand colour
  due: '#b45309', // amber-700, see NODE_DUE_LIGHT
};

export const NODE_TIER_DARK: Record<MemoryTier, string> = {
  // Learned is the saturated violet and fading the pale lavender, the same
  // story as light: fading is learned colour losing its saturation. In dark
  // that makes fading the BRIGHTER of the two, which is fine; what reads as
  // "washed out" is the loss of colour, not the change in lightness.
  strong: '#9a7ff0',
  fresh: '#9a7ff0',
  fading: '#d4c4ff',
  new: '#646a8a',
  due: '#f7ad31', // --warning dark
};

/**
 * The neutral ink, for a concept with nothing attached to it.
 * `--muted-foreground`.
 */
export const NODE_INK_LIGHT = '78, 86, 115'; // #4E5673 --muted-foreground
export const NODE_INK_DARK = '172, 181, 211'; // #ACB5D3

/**
 * Hover and selection: a RING in the brand indigo, around the node's own
 * colour.
 *
 * It used to repaint the node in `--primary`, which is near-black in light,
 * and every link of the focused concept took the same near-black. A pointer
 * shouldn't turn the map black, and repainting the fill also hid the very
 * state the reader was pointing at to find out. A ring keeps the colour and
 * still says "this one".
 */
export const NODE_HIGHLIGHT_LIGHT = '#4f46e5'; // indigo-600
export const NODE_HIGHLIGHT_DARK = '#b793ff'; // --primary dark
export const NODE_HIGHLIGHT_SOFT_LIGHT = 'rgba(79, 70, 229, 0.16)';
export const NODE_HIGHLIGHT_SOFT_DARK = 'rgba(183, 147, 255, 0.2)';

export const LABEL_LIGHT = '#334155'; // slate-700 - better contrast than 600
export const LABEL_DARK = '#f4f6ff'; // --foreground
export const LABEL_PILL_LIGHT = 'rgba(255, 255, 255, 0.85)'; // behind the text
export const LABEL_PILL_DARK = 'rgba(27, 29, 35, 0.85)'; // --card

/**
 * Links, as a light tint of the brand rather than a grey.
 *
 * They were the slate ink at 45%, which on a pale card reads as dark pencil:
 * hundreds of them drew a grey web over the indigo nodes, heavier than the
 * nodes themselves. A pale indigo keeps the lines in the same family as what
 * they connect and lets them sit BEHIND the nodes, where structure belongs.
 *
 * A focused concept's links take the ring colour, so "these are its links"
 * reads as part of the same highlight.
 */
export const LINK_FADED_LIGHT = 'rgba(79, 70, 229, 0.22)';
export const LINK_FADED_DARK = 'rgba(183, 147, 255, 0.22)';
export const LINK_DIM_LIGHT = 'rgba(79, 70, 229, 0.06)';
export const LINK_DIM_DARK = 'rgba(183, 147, 255, 0.06)';
export const LINK_FOCUS_LIGHT = 'rgba(79, 70, 229, 0.85)';
export const LINK_FOCUS_DARK = 'rgba(183, 147, 255, 0.9)';

/**
 * Light mode's lesson glow: a soft wash of its own hue behind its concepts, bright
 * where they are dense and fading out at the edges, so a lesson reads as one
 * region and neighbouring lessons blend where they meet (drawn by
 * `MindspaceGraph.drawGlow`; hues assigned by `assignLessonHues`).
 *
 * Muted, and never amber or red: amber is the "worth a review" dot, and a
 * warm wash under it would steal its one job. Six hues are enough because
 * assignment only has to keep NEIGHBOURS apart.
 *
 * Kept faint so the dots' memory colours stay the loudest thing on the map:
 * `GLOW_ALPHA_*` is a blob's alpha at its centre; it fades towards the rim.
 */
export const LESSON_HUES: ReadonlyArray<readonly [number, number, number]> = [
  [99, 102, 241], // indigo
  [14, 165, 233], // sky
  [20, 184, 166], // teal
  [168, 85, 247], // purple
  [16, 185, 129], // emerald
  [236, 72, 153], // pink
];
export const GLOW_ALPHA_LIGHT = 0.16;
/** Dark mode's lesson haze, added as light over the nebula image. */
export const GLOW_ALPHA_DARK = 0.2;

/**
 * Amber for a concept that is due, the one state that asks the student for
 * something, so the one that gets a hue of its own.
 *
 * A true orange, not `--warning-emphasis`. That token is `#805500`, a dark
 * olive-gold: it clears contrast handsomely and then reads as "another dark
 * dot" beside the ink. Amber-700 is 4.66:1 and nowhere near the indigo.
 */
export const NODE_DUE_LIGHT = '#b45309'; // amber-700
export const NODE_DUE_DARK = '#f7ad31'; // --warning dark

/**
 * The colours the canvas paints with, for one theme.
 *
 * A function rather than a row of ternaries in the component body, because a
 * canvas takes an `rgb()` string and not a class — so unlike every other
 * component here, the theme branch has to happen in JavaScript. Lifting them
 * out keeps that job named, and keeps the component under the complexity limit
 * it was already sitting exactly on.
 */
export function canvasPalette(isDark: boolean) {
  const ink = isDark ? NODE_INK_DARK : NODE_INK_LIGHT;
  const tiers = isDark ? NODE_TIER_DARK : NODE_TIER_LIGHT;
  return {
    /** A concept's colour for its memory state. See `NODE_TIER_LIGHT`. */
    nodeTier: (tier: MemoryTier | undefined) => tiers[tier ?? 'strong'],
    /** The neutral ink, for links and for orphans. Takes an alpha. */
    nodeInk: (alpha: number) => `rgba(${ink}, ${alpha})`,
    nodeDue: isDark ? NODE_DUE_DARK : NODE_DUE_LIGHT,
    labelColor: isDark ? LABEL_DARK : LABEL_LIGHT,
    labelPill: isDark ? LABEL_PILL_DARK : LABEL_PILL_LIGHT,
    highlight: isDark ? NODE_HIGHLIGHT_DARK : NODE_HIGHLIGHT_LIGHT,
    /** Behind the focus ring, so it reads on any fill: the card colour. */
    ringGap: isDark ? '#1b1d23' : '#f4f6ff',
    highlightSoft: isDark
      ? NODE_HIGHLIGHT_SOFT_DARK
      : NODE_HIGHLIGHT_SOFT_LIGHT,
  };
}

/** Everything outside the focused concept's neighbourhood. */
export const FADE_ALPHA = 0.18;

/**
 * A concept whose source lesson is gone, drawn in the neutral ink: set apart
 * from the memory states, not one of them.
 */
export const ORPHAN_ALPHA = 0.7;

/**
 * A paused concept's ring, in the neutral ink. Paused is not a memory state,
 * so it takes no colour from the legend's ramp; it is told apart by being
 * hollow.
 */
export const PAUSED_ALPHA = 0.85;
